import type { CropRect, Preset, Scenario } from "./types";

export const PRAYER_MASTER_URL = "/scenes/2k/prayer.master.jpg";
export const PRAYER_MASTER_W = 2048;
export const PRAYER_MASTER_H = 1143;

// Stage geometry (per multiview-visual-contract.md): the brother offering
// prayer stands behind the centered lectern with head bowed in dignified
// posture; chairman's mic on a chrome floor stand is alone on audience-left;
// audience-right is empty stage. CO-160 §4 reserves Stage Wide for prayers,
// so every cam should default to a Stage Wide-class crop. Crops below are
// normalized (0..1) over the 2048x1143 2K master generated from the original
// 5504x3072 Gemini 3 Pro Image master (2026-05-08).
export const PRAYER_PRESETS = {
  // CO-160 §4 Stage Wide — full stage edge-to-edge, the canonical prayer shot.
  stageWide: { x: 0.04, y: 0.04, width: 0.92, height: 0.88 },
  // Slight stage-right favor (CAM 1 flavor): drops the chairman's mic side.
  stageWideRight: { x: 0.18, y: 0.06, width: 0.78, height: 0.84 },
  // Slight stage-left favor (CAM 3 flavor): includes the chairman's mic stand.
  stageWideLeft: { x: 0.04, y: 0.06, width: 0.78, height: 0.84 },
  // Lectern MCU on the bowed-head speaker for centered cam.
  lecternMcu: { x: 0.42, y: 0.28, width: 0.16, height: 0.42 },
  // Wider lectern context with the bowed speaker.
  lecternWide: { x: 0.36, y: 0.18, width: 0.28, height: 0.58 },
} as const satisfies Record<string, CropRect>;

// Per-cam preset library for the prayer scenario. Display order is the array
// order; AW-RP50 numeric buttons (1..10) bind to the first 10.
// CAM 1 (audience-left of array) → favors stage-right framings.
// CAM 2 (centered) → centered lectern, bowed speaker.
// CAM 3 (audience-right of array) → audience-left / chairman's mic side.
const cam1Presets: Preset[] = [
  { id: "stage-wide-right", label: "Stage Wide R", cam: "cam1", scenarioId: "prayer", crop: PRAYER_PRESETS.stageWideRight },
  { id: "stage-wide", label: "Stage Wide", cam: "cam1", scenarioId: "prayer", crop: PRAYER_PRESETS.stageWide },
];

const cam2Presets: Preset[] = [
  { id: "stage-wide", label: "Stage Wide", cam: "cam2", scenarioId: "prayer", crop: PRAYER_PRESETS.stageWide },
  { id: "lectern-wide", label: "Lectern Wide", cam: "cam2", scenarioId: "prayer", crop: PRAYER_PRESETS.lecternWide },
  { id: "lectern-mcu", label: "Lectern MCU", cam: "cam2", scenarioId: "prayer", crop: PRAYER_PRESETS.lecternMcu },
];

const cam3Presets: Preset[] = [
  { id: "stage-wide-left", label: "Stage Wide L", cam: "cam3", scenarioId: "prayer", crop: PRAYER_PRESETS.stageWideLeft },
  { id: "stage-wide", label: "Stage Wide", cam: "cam3", scenarioId: "prayer", crop: PRAYER_PRESETS.stageWide },
];

export const PRAYER: Scenario = {
  id: "prayer",
  label: "Prayer",
  master: {
    url: PRAYER_MASTER_URL,
    width: PRAYER_MASTER_W,
    height: PRAYER_MASTER_H,
  },
  presets: { cam1: cam1Presets, cam2: cam2Presets, cam3: cam3Presets },
  defaults: {
    cam1: "stage-wide-right",
    cam2: "stage-wide",
    cam3: "stage-wide-left",
  },
};
